// ============================================================================
// ExercisePicker — a Drawer over the exercise catalog + the trainer's
// favorites. Search by name, filter by muscle / equipment, toggle Favorites vs
// Catalog, and add exercises into the program editor. Multi-select: each row
// flips to an "Added" state so the trainer can queue several in one visit.
// Data flows through the typed hooks (useExercises / useFavorites); already-in-
// program exercises are shown as added so they can't be double-queued.
// ============================================================================
import { useMemo, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Badge, Button, SelectField, TextField } from "../../components";
import type { SelectOption } from "../../components";
import { useExercises, useFavorites } from "../../hooks/exercises";
import type { Exercise, ExerciseFilter } from "../../api";
import { Drawer } from "./Drawer";
import { IconCheck, IconPlus, IconSearch } from "./icons";
import { exerciseMeta, exerciseName } from "./lib";

interface ExercisePickerProps {
  open: boolean;
  onClose: () => void;
  /** Exercise ids already in the program (shown as added). */
  existingIds: ReadonlySet<number>;
  onAdd: (exercise: Exercise) => void;
}

type Tab = "catalog" | "favorites";

// Muscle / equipment filter values — match the backend catalog vocabulary.
const MUSCLE_VALUES = [
  "chest",
  "back",
  "shoulders",
  "biceps",
  "triceps",
  "quadriceps",
  "hamstrings",
  "glutes",
  "calves",
  "abdominals",
];
const EQUIPMENT_VALUES = [
  "barbell",
  "dumbbell",
  "machine",
  "cable",
  "kettlebell",
  "bodyweight",
  "bands",
];

export function ExercisePicker({
  open,
  onClose,
  existingIds,
  onAdd,
}: ExercisePickerProps) {
  const { t, i18n } = useTranslation("templates");
  const locale = i18n.language;

  const [tab, setTab] = useState<Tab>("catalog");
  const [text, setText] = useState("");
  const [muscle, setMuscle] = useState("");
  const [equipment, setEquipment] = useState("");
  // Track ids added this session so newly-queued rows reflect instantly.
  const [justAdded, setJustAdded] = useState<Set<number>>(new Set());

  const filter: ExerciseFilter = useMemo(() => {
    const f: ExerciseFilter = {};
    if (text.trim()) f.text = text.trim();
    if (muscle) f.muscle = muscle;
    if (equipment) f.equipment = equipment;
    return f;
  }, [text, muscle, equipment]);

  const catalogQuery = useExercises(tab === "catalog" ? filter : {});
  const favoritesQuery = useFavorites();

  const muscleOptions: SelectOption[] = MUSCLE_VALUES.map((v) => ({
    value: v,
    label: t(`picker.muscles.${v}`),
  }));
  const equipmentOptions: SelectOption[] = EQUIPMENT_VALUES.map((v) => ({
    value: v,
    label: t(`picker.equipment.${v}`),
  }));

  // Favorites are filtered client-side (the favorites endpoint isn't filtered).
  const favorites = useMemo(() => {
    const list = favoritesQuery.data ?? [];
    const q = text.trim().toLowerCase();
    return list.filter((ex) => {
      if (q) {
        const n = `${ex.name_en ?? ""} ${ex.name_zh ?? ""}`.toLowerCase();
        if (!n.includes(q)) return false;
      }
      if (muscle && !(ex.primary_muscles ?? "").toLowerCase().includes(muscle)) {
        return false;
      }
      if (equipment && !(ex.equipment ?? "").toLowerCase().includes(equipment)) {
        return false;
      }
      return true;
    });
  }, [favoritesQuery.data, text, muscle, equipment]);

  const list = tab === "catalog" ? catalogQuery.data ?? [] : favorites;
  const isLoading =
    tab === "catalog" ? catalogQuery.isLoading : favoritesQuery.isLoading;

  const added = (id: number) => existingIds.has(id) || justAdded.has(id);

  const handleAdd = (ex: Exercise) => {
    onAdd(ex);
    setJustAdded((prev) => {
      const next = new Set(prev);
      next.add(ex.id);
      return next;
    });
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      wide
      title={t("picker.title")}
      subtitle={t("picker.subtitle")}
      footer={
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm font-bold text-body-soft">
            {t("picker.selected", { count: justAdded.size })}
          </span>
          <Button onClick={onClose}>{t("actions.done")}</Button>
        </div>
      }
    >
      <div className="space-y-5">
        {/* Tabs: catalog / favorites */}
        <div
          role="tablist"
          aria-label={t("picker.title")}
          className="inline-flex items-center gap-1 rounded-pill bg-surface-sunken p-1.5 ring-1 ring-line"
        >
          {(["catalog", "favorites"] as const).map((id) => {
            const isActive = id === tab;
            return (
              <button
                key={id}
                role="tab"
                aria-selected={isActive}
                type="button"
                onClick={() => setTab(id)}
                className={
                  "relative rounded-pill px-4 py-2 font-display text-sm font-bold transition-colors duration-fast " +
                  (isActive ? "text-white" : "text-body-soft hover:text-body")
                }
              >
                {isActive && (
                  <motion.span
                    layoutId="picker-tab-pill"
                    className="absolute inset-0 -z-10 rounded-pill bg-grad-coral shadow-volt"
                    transition={{ type: "spring", stiffness: 480, damping: 36 }}
                  />
                )}
                <span className="relative z-10">
                  {id === "catalog" ? t("picker.catalog") : t("picker.favorites")}
                </span>
              </button>
            );
          })}
        </div>

        {/* Search + filters */}
        <div className="space-y-3">
          <TextField
            label={t("picker.search")}
            hideLabel
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={t("picker.searchPlaceholder")}
            iconLeft={<IconSearch width={18} height={18} />}
            type="search"
          />
          <div className="grid grid-cols-2 gap-3">
            <SelectField
              label={t("picker.filterMuscle")}
              hideLabel
              value={muscle}
              onChange={(e) => setMuscle(e.target.value)}
              placeholder={t("picker.filterMuscle")}
              options={[{ value: "", label: t("picker.all") }, ...muscleOptions]}
            />
            <SelectField
              label={t("picker.filterEquipment")}
              hideLabel
              value={equipment}
              onChange={(e) => setEquipment(e.target.value)}
              placeholder={t("picker.filterEquipment")}
              options={[{ value: "", label: t("picker.all") }, ...equipmentOptions]}
            />
          </div>
        </div>

        {/* Results */}
        {isLoading ? (
          <PickerSkeleton />
        ) : list.length === 0 ? (
          <PickerEmpty tab={tab} />
        ) : (
          <ul className="space-y-2">
            <AnimatePresence initial={false}>
              {list.map((ex) => (
                <PickerRow
                  key={ex.id}
                  ex={ex}
                  locale={locale}
                  added={added(ex.id)}
                  onAdd={() => handleAdd(ex)}
                />
              ))}
            </AnimatePresence>
          </ul>
        )}
      </div>
    </Drawer>
  );
}

function PickerRow({
  ex,
  locale,
  added,
  onAdd,
}: {
  ex: Exercise;
  locale: string | undefined;
  added: boolean;
  onAdd: () => void;
}) {
  const { t } = useTranslation("templates");
  const reduce = useReducedMotion();
  const name = exerciseName(ex, locale) || t("item.unnamed");
  const meta = exerciseMeta(ex);

  return (
    <motion.li
      layout={!reduce}
      initial={reduce ? false : { opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.98 }}
      transition={{ duration: 0.2, ease: [0.22, 0.61, 0.36, 1] }}
      className="flex items-center gap-3 rounded-md bg-surface px-3 py-2.5 ring-1 ring-line shadow-xs"
    >
      <div className="min-w-0 flex-1">
        <p className="truncate font-display text-sm font-bold text-body">{name}</p>
        {meta && <p className="truncate text-xs text-body-soft">{cap(meta)}</p>}
      </div>
      {ex.is_custom ? (
        <Badge tone="ember" className="shrink-0">
          {/* tiny "custom" hint reuses the catalog tone; label optional */}
          ★
        </Badge>
      ) : null}
      <Button
        size="sm"
        variant={added ? "ghost" : "primary"}
        onClick={onAdd}
        disabled={added}
        iconLeft={
          added ? <IconCheck width={16} height={16} /> : <IconPlus width={16} height={16} />
        }
        className="shrink-0"
      >
        {added ? t("picker.added") : t("picker.add")}
      </Button>
    </motion.li>
  );
}

function PickerSkeleton() {
  return (
    <ul className="space-y-2" aria-hidden>
      {Array.from({ length: 5 }).map((_, i) => (
        <li
          key={i}
          className="flex items-center gap-3 rounded-md bg-surface px-3 py-2.5 ring-1 ring-line"
        >
          <div className="min-w-0 flex-1 space-y-2">
            <div className="h-3.5 w-2/5 rounded-pill bg-surface-sunken" />
            <div className="h-2.5 w-1/4 rounded-pill bg-surface-sunken" />
          </div>
          <div className="h-9 w-16 rounded-pill bg-surface-sunken" />
        </li>
      ))}
    </ul>
  );
}

function PickerEmpty({ tab }: { tab: Tab }) {
  const { t } = useTranslation("templates");
  const isFav = tab === "favorites";
  return (
    <div className="rounded-md bg-surface-sunken px-6 py-12 text-center">
      <p className="font-display text-base font-bold text-body">
        {isFav ? t("picker.favEmpty") : t("picker.empty")}
      </p>
      <p className="mt-1 text-sm text-body-soft">
        {isFav ? t("picker.favEmptyBlurb") : t("picker.emptyBlurb")}
      </p>
    </div>
  );
}

function cap(s: string): string {
  return s
    .split(/[\s,·]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" · ");
}
